import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { defaultRetry } from './react-query';
import { useApi } from './useApi';
import {
  MasterlistCheckResponse,
  MasterlistJournalCheck,
} from '../../types/masterlistCheck.types';

export const masterlistCheckQueryKey = (agreementId?: string) =>
  agreementId ? ['masterlistCheck', agreementId] : ['masterlistCheck'];

/** Compares a saved agreement's journals with the SciFree masterlist. */
export const useMasterlistCheck = (
  agreementId: string | undefined,
  enabled: boolean
) => {
  const { makeApiRequest } = useApi();
  const masterlistCheckEnabled = enabled && !!agreementId;

  const { data, isPending, error } = useQuery<MasterlistCheckResponse>({
    queryKey: masterlistCheckQueryKey(agreementId),
    queryFn: () =>
      makeApiRequest<MasterlistCheckResponse>(
        `/agreements/${agreementId}/journals/masterlist-check`,
        'GET'
      ),
    enabled: masterlistCheckEnabled,
    // A full server-side comparison; saves, moves and masterlist applies already invalidate it,
    // so don't rerun it every time the tab regains focus.
    refetchOnWindowFocus: false,
    // Each try is a full comparison, so at most one retry before showing it failed, not the
    // default two. Still none for auth errors, like every other query.
    retry: (failureCount, error) =>
      failureCount < 1 && defaultRetry(failureCount, error),
  });
  // A disabled query can still return cached data; it doesn't apply while the check is off.
  const masterlistCheck = masterlistCheckEnabled ? data : undefined;

  const checksByJournalId = useMemo(() => {
    const map = new Map<string, MasterlistJournalCheck>();
    masterlistCheck?.journals.forEach((check) =>
      map.set(check.journal_id, check)
    );
    return map;
  }, [masterlistCheck]);

  return {
    masterlistCheckEnabled,
    masterlistCheck,
    checksByJournalId,
    // isPending, not isLoading: a paused query (e.g. offline) isn't fetching but hasn't failed.
    isMasterlistCheckLoading: masterlistCheckEnabled && isPending,
    masterlistCheckError: error,
  };
};

export type MasterlistCheckState = ReturnType<typeof useMasterlistCheck>;
